// src/components/Dashboard/ClosingBuilder.jsx
//
// Closing Statement reconstruido (2026-09-23). Uno por cada cheque del
// reclamo (un reclamo puede tener varios: inicial, parcial, suplemental,
// final, RCV). El formulario tiene los mismos renglones que el documento;
// cada costo "(If Applicable)" puede llevar monto o quedar vacío.
//
// Ciclo de cada closing: guardar → PDF → "Enviar a firmar" (registro manual
// de la fecha y un enlace opcional; no hay firma electrónica integrada) →
// "Subir firmado" respalda el PDF en Hostinger → firmado = montos bloqueados.
//
// Dónde vive:
//   mode="full"  en el panel de pasos, en "Cobro en curso" y "RCV" (Fase 4):
//                se crean, editan y avanzan los closings.
//   mode="list"  en Documentos: solo la lista para ver/descargar, en
//                cualquier paso.
//   check={…}   dentro de la tarjeta de un cheque (Fase 4): solo el Closing
//                de ese cheque (uno por cheque), con número, fecha y monto del
//                cheque ya cargados. En el paso "Breakdown" también lo edita
//                el PA (canEdit); mandarlo a firmar y subir el firmado es del
//                equipo CSR.
// Sin check: solo el equipo CSR crea/edita/avanza; el PA ve y descarga.
//
// Membrete: pendiente (el cliente manda los membretes corregidos).
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CLOSING_FEES, PAYMENT_TYPES, buildClosingData, money, suggestPaFee } from '../../documents/closingData';
import { generateClosingPdf } from '../../documents/closingPdf';
import { downloadPdf } from '../../documents/pdfFill';
import { attachSignedClosing, closingTotals, listClosings, markClosingGenerated, markClosingSent, saveClosing } from '../../services/closingService';
import { updateClaimRecordFields } from '../../services/claimRecordService';
import { openBackup, validateBackupFile } from '../../services/backupService';
import { insuredNames } from '../../documents/spolData';
import './ClaimFlow.css';
import { localTodayISO } from '../../utils/dateHelpers';

const today = () => localTodayISO();
const blankClosing = (claimRecord, check = null, prefill = {}) => ({
  checkNumber: check?.checkNumber || '', checkDate: check?.checkDate || '', checkAmount: check?.amount || '',
  feePercent: check?.adjusterPercentage || claimRecord?.agreedFeePercent || '', paFee: '',
  ...Object.fromEntries(CLOSING_FEES.map(fee => [fee.key, prefill[fee.key] ?? ''])),
  paymentType: check?.paymentType || '', date: today(),
  checkId: check?.id || null
});
// Documento guardado → valores del formulario (null → '').
const toForm = saved => Object.fromEntries(Object.entries({ ...blankClosing(), ...saved }).map(([k, v]) => [k, v ?? '']));
const safeName = value => String(value || '').replace(/[\\/:*?"<>|]+/g, '').trim();

// Datos del encabezado del Closing que vienen del reclamo. Si faltan, se
// pueden escribir en el formulario y se guardan en el reclamo (así también
// los usan la LOR, la SPOL y el resto de la ficha).
const CLAIM_KEYS = ['claimNumber', 'policyNumber', 'insuranceCompanyName'];
const CLAIM_LABELS = { claimNumber: 'Claim Number:', policyNumber: 'Policy Number', insuranceCompanyName: 'Insurance Company:' };

const STATUS_LABELS = {
  draft: ['Borrador', 'Draft', 'cd-status-pending'],
  generated: ['PDF generado', 'PDF generated', 'cd-status-pending'],
  sent: ['Enviado a firmar', 'Sent for signature', 'cd-status-pending'],
  signed: ['Firmado ✓', 'Signed ✓', 'cd-status-paid']
};

const ClosingBuilder = ({ claimRecord: claimFromProps, language, isAdmin, onClaimChange, mode = 'full', check = null, canEdit, prefill = {}, onClosingsChange }) => {
  const es = language === 'es';
  // Editar montos (borrador) y avanzar el ciclo (generar / enviar / subir el firmado): cualquier
  // rol con acceso al reclamo. Las reglas de Firestore y backup.php lo validan igual.
  const canManage = canEdit ?? (mode === 'full');
  const canAdvance = mode === 'full';
  const [closings, setClosings] = useState([]);
  const [editing, setEditing] = useState(null); // { id|null, form }
  const [action, setAction] = useState(null); // { type: 'send'|'sign', closing, date, url, file }
  const [busy, setBusy] = useState(false);
  const [claimFields, setClaimFields] = useState(() => Object.fromEntries(CLAIM_KEYS.map(k => [k, claimFromProps[k] || ''])));
  const claimRecord = useMemo(() => ({ ...claimFromProps, ...claimFields }), [claimFromProps, claimFields]);

  const reload = () => listClosings(claimRecord.id)
    .then(list => {
      const shown = check ? list.filter(c => c.checkId === check.id) : list;
      setClosings(shown);
      if (onClosingsChange) onClosingsChange(shown);
    })
    .catch(error => console.error('Error loading closings:', error));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [claimRecord.id]);


  const form = editing?.form;
  const result = useMemo(() => (form ? buildClosingData(claimRecord, form, language) : null), [claimRecord, form, language]);
  const setField = (key, value) => setEditing(prev => ({ ...prev, form: { ...prev.form, [key]: value } }));
  const totals = closingTotals(closings);
  const duplicate = form?.checkNumber
    ? closings.find(c => c.id !== editing.id && (c.checkNumber || '').trim().toLowerCase() === form.checkNumber.trim().toLowerCase())
    : null;

  const run = async (fn, successMessage) => {
    setBusy(true);
    try {
      const out = await fn();
      await reload();
      if (successMessage) toast.success(successMessage);
      return out ?? true;
    } catch (error) {
      console.error('Closing action failed:', error);
      toast.error(error.message === 'SIGNED_LOCKED'
        ? (es ? 'Este Closing ya está firmado; no se puede modificar.' : 'This Closing is signed and cannot be changed.')
        : (es ? `No se pudo completar: ${error.message}` : `Could not complete: ${error.message}`));
      return null;
    } finally {
      setBusy(false);
    }
  };

  // En la tarjeta de un cheque, al guardar se cierra el editor y queda la
  // tarjeta del Closing con sus botones (PDF, enviar, subir firmado).
  const save = ({ keepOpen = !check } = {}) => run(async () => {
    const changed = Object.fromEntries(CLAIM_KEYS.filter(k => (claimFields[k] || '').trim() !== (claimFromProps[k] || '')).map(k => [k, claimFields[k].trim()]));
    if (Object.keys(changed).length) {
      await updateClaimRecordFields(claimFromProps.id, changed, CLAIM_KEYS);
      if (onClaimChange) onClaimChange(changed);
    }
    const toSave = check ? { ...form, checkId: check.id, checkNumber: check.checkNumber || '', checkDate: check.checkDate || '' } : form;
    const saved = await saveClosing(claimRecord, toSave, editing.id);
    if (keepOpen) setEditing(prev => ({ ...prev, id: saved.id }));
    else setEditing(null);
    return saved;
  }, es ? 'Closing guardado' : 'Closing saved');

  const download = async (closingForm, saved) => {
    try {
      const { values } = buildClosingData(claimRecord, closingForm, language);
      const bytes = await generateClosingPdf(values);
      const name = ['Closing Statement', safeName(claimRecord.clientName), safeName(claimRecord.claimNumber), safeName(closingForm.checkNumber && `Check ${closingForm.checkNumber}`), safeName(closingForm.paymentType)].filter(Boolean).join(' - ');
      downloadPdf(bytes, `${name}.pdf`);
      if (canAdvance && saved?.id) { await markClosingGenerated(saved); reload(); }
    } catch (error) {
      console.error('Error generating closing:', error);
      toast.error(es ? 'No se pudo generar el PDF' : 'Could not generate the PDF');
    }
  };


  const confirmAction = () => {
    if (action.type === 'send') {
      run(() => markClosingSent(action.closing.id, { sentDate: action.date, signatureUrl: action.url }), es ? 'Marcado como enviado a firmar' : 'Marked as sent for signature')
        .then(ok => ok && setAction(null));
      return;
    }
    try { validateBackupFile(action.file); } catch (error) { toast.error(error.message); return; }
    run(() => attachSignedClosing(action.closing, action.file, action.date, claimRecord), es ? 'Closing firmado y respaldado · se creó el cheque para el cobro de CSR' : 'Signed Closing backed up · CSR commission check created')
      .then(ok => ok && setAction(null));
  };

  // Renglón del formulario con la misma etiqueta que el documento.
  const row = (text, children, className = '') => (
    <div className={`closing-row ${className}`}>
      <span className="closing-row-label">{text}</span>
      <span className="closing-row-input">{children}</span>
    </div>
  );
  const amountInput = (key, placeholder = '$') => (
    <input type="number" step="0.01" min="0" className="cc-input"
      value={form[key]} placeholder={placeholder} onChange={e => setField(key, e.target.value)} />
  );
  const statusBadge = status => {
    const [lEs, lEn, cls] = STATUS_LABELS[status || 'draft'] || STATUS_LABELS.draft;
    return <span className={`cd-status-badge ${cls}`}>{es ? lEs : lEn}</span>;
  };

  return (
    <div className="flow-block">
      <div className="closing-head">
        <span className="closing-head-title">{check ? (es ? 'Breakdown y Closing Statement de este cheque' : 'Breakdown & Closing Statement for this check') : (es ? 'Closing Statements · uno por cheque' : 'Closing Statements · one per check')}</span>
        {canManage && !editing && !(check && closings.length > 0) && (
          <button type="button" className="cd-btn cd-btn-check" onClick={() => setEditing({ id: null, form: blankClosing(claimRecord, check, prefill) })}>
            <i className="fa-solid fa-plus"></i> {check ? (es ? 'Armar breakdown' : 'Build breakdown') : (es ? 'Nuevo Closing' : 'New Closing')}
          </button>
        )}
      </div>
      {mode === 'list' && isAdmin && (
        <p className="flow-note">
          {es ? 'Cada Closing se arma dentro de su cheque, en la Fase 4 (Cobro): pasos "Breakdown" y "Closing Statement".' : 'Each Closing is built inside its check in Phase 4: "Breakdown" and "Closing Statement" steps.'}
        </p>
      )}
      {closings.length === 0 && !editing && <p className="flow-note">{check ? (es ? 'Todavía no se armó el breakdown de este cheque.' : 'No breakdown yet for this check.') : (es ? 'Todavía no hay Closings.' : 'No Closings yet.')}</p>}

      {closings.length > 0 && !editing && (
        <>
          <div className="closing-list">
            {closings.map(c => (
              <div key={c.id} className="closing-item">
                <div className="closing-item-head">
                  <div>
                    <div className="closing-item-check">{c.checkNumber ? `${es ? 'Cheque' : 'Check'} #${c.checkNumber}` : (es ? 'Cheque sin número' : 'Check without number')}</div>
                    <div className="closing-item-meta">{[c.paymentType, c.checkDate].filter(Boolean).join(' · ') || '—'}</div>
                    {c.docusignSignedDate && c.status !== 'signed' && (
                      <div className="closing-item-meta">{es ? `Firmado en DocuSign el ${c.docusignSignedDate}. Sube el PDF firmado.` : `Signed in DocuSign on ${c.docusignSignedDate}. Upload the signed PDF.`}</div>
                    )}
                  </div>
                  {statusBadge(c.status)}
                </div>
                <div className="closing-item-amounts">
                  <span>{es ? 'Cheque' : 'Check'}<strong>{money(c.checkAmount)}</strong></span>
                  <span>PA<strong>{money(c.paFeeApplied)}</strong></span>
                  <span>{es ? 'Neto al cliente' : 'Net to client'}<strong>{money(c.netToClient)}</strong></span>
                </div>
                <div className="closing-item-actions">
                  <button type="button" className="cd-btn cd-btn-invoice" onClick={() => download(toForm(c), c)}><i className="fa-solid fa-file-pdf"></i> PDF</button>
                  {c.signedPdf?.storageKey && (
                    <button type="button" className="cd-btn cd-btn-invoice" onClick={() => openBackup(c.signedPdf.storageKey).catch(e => toast.error(e.message))}>
                      <i className="fa-solid fa-file-signature"></i> {es ? 'Ver firmado' : 'View signed'}
                    </button>
                  )}
                  {c.signedCertificate?.storageKey && (
                    <button type="button" className="cd-btn cd-btn-invoice" onClick={() => openBackup(c.signedCertificate.storageKey).catch(e => toast.error(e.message))}>
                      <i className="fa-solid fa-certificate"></i> {es ? 'Certificado' : 'Certificate'}
                    </button>
                  )}
                  {canManage && c.status !== 'signed' && (
                    <button type="button" className="cd-btn" onClick={() => setEditing({ id: c.id, form: toForm(c) })}><i className="fa-solid fa-pen"></i> {es ? 'Editar' : 'Edit'}</button>
                  )}
                  {canAdvance && c.status !== 'signed' && (
                    <>
                      {/* Botón provisional: registra el envío a mano (fecha + enlace opcional).
                          El diseño final del envío a firmar queda para el equipo de UX. */}
                      {['draft', 'generated'].includes(c.status || 'draft') && (
                        <button type="button" className="cd-btn cd-btn-check" disabled={busy} onClick={() => setAction({ type: 'send', closing: c, date: today(), url: c.signatureUrl || c.docusignUrl || '' })}><i className="fa-solid fa-file-signature"></i> {es ? 'Enviar a firmar' : 'Send for signature'}</button>
                      )}
                      {c.status === 'sent' && <button type="button" className="cd-btn cd-btn-check" onClick={() => setAction({ type: 'sign', closing: c, date: today(), file: null })}><i className="fa-solid fa-upload"></i> {es ? 'Subir firmado' : 'Upload signed'}</button>}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          {!check && <div className="closing-totals">
            <div className="closing-total"><span>{es ? 'Recibido del seguro' : 'Received'}</span><strong>{money(totals.received)}</strong></div>
            <div className="closing-total"><span>{es ? 'Honorarios PA' : 'PA fees'}</span><strong>{money(totals.paFees)}</strong></div>
            <div className="closing-total"><span>{es ? 'Otros costos' : 'Other fees'}</span><strong>{money(Math.round((totals.received - totals.paFees - totals.net) * 100) / 100)}</strong></div>
            <div className="closing-total"><span>{es ? 'Neto al cliente' : 'Net to client'}</span><strong>{money(totals.net)}</strong></div>
          </div>}
        </>
      )}

      {action && ['send', 'sign'].includes(action.type) && (
        <div className="flow-subpanel">
          <div className="flow-subpanel-title">
            {action.type === 'send' ? (es ? 'Mandar a firmar' : 'Send for signature') : (es ? 'Respaldar el Closing firmado' : 'Back up the signed Closing')}
            <span className="flow-muted"> · {action.closing.checkNumber ? `#${action.closing.checkNumber} · ` : ''}{action.closing.paymentType} {money(action.closing.checkAmount)}</span>
          </div>
          <div className="cc-grid">
            <div className="cc-input-group">
              <label className="cc-label">{action.type === 'send' ? (es ? 'Fecha de envío' : 'Sent date') : (es ? 'Fecha de firma' : 'Signed date')}</label>
              <input type="date" className="cc-input" value={action.date} onChange={e => setAction(a => ({ ...a, date: e.target.value }))} />
            </div>
            {action.type === 'send' ? (
              <div className="cc-input-group">
                <label className="cc-label">{es ? 'Enlace del documento enviado (opcional)' : 'Sent document link (optional)'}</label>
                <input type="url" className="cc-input" value={action.url} onChange={e => setAction(a => ({ ...a, url: e.target.value }))} placeholder="https://" />
              </div>
            ) : (
              <div className="cc-input-group">
                <label className="cc-label">{es ? 'PDF firmado (máx. 15 MB)' : 'Signed PDF (max 15 MB)'}</label>
                <input type="file" accept="application/pdf" className="cc-input" onChange={e => setAction(a => ({ ...a, file: e.target.files?.[0] || null }))} />
              </div>
            )}
          </div>
          <div className="flow-actions">
            <button type="button" className="cd-btn cd-btn-check" disabled={busy || (action.type === 'sign' && !action.file)} onClick={confirmAction}>
              {action.type === 'send' ? (es ? 'Confirmar envío' : 'Confirm sent') : (es ? 'Subir y marcar firmado' : 'Upload & mark signed')}
            </button>
            <button type="button" className="cd-btn" onClick={() => setAction(null)}>{es ? 'Cancelar' : 'Cancel'}</button>
          </div>
        </div>
      )}

      {editing && (
        <div className="flow-subpanel">
          <div className="flow-subpanel-title">{editing.id ? (es ? 'Editar Closing Statement' : 'Edit Closing Statement') : (es ? 'Nuevo Closing Statement' : 'New Closing Statement')}</div>
          {check ? (
            <p className="flow-note">
              {es ? `Cheque ${check.checkNumber ? `#${check.checkNumber}` : 'sin número'} · ${check.checkDate || 'sin fecha'} · ${money(check.amount) || '$0'} — se editan en la tarjeta del cheque.` : `Check ${check.checkNumber ? `#${check.checkNumber}` : 'without number'} · ${check.checkDate || 'no date'} · ${money(check.amount) || '$0'} — edit them on the check card.`}
            </p>
          ) : (
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label">{es ? 'Número del cheque' : 'Check number'}</label>
                <input type="text" className="cc-input" value={form.checkNumber} onChange={e => setField('checkNumber', e.target.value)} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label">{es ? 'Fecha del cheque' : 'Check date'}</label>
                <input type="date" className="cc-input" value={form.checkDate} onChange={e => setField('checkDate', e.target.value)} />
              </div>
            </div>
          )}
          {duplicate && (
            <p className="closing-warning">{es ? `Ya hay un Closing para el cheque #${duplicate.checkNumber} (${duplicate.paymentType || 'sin tipo'}, ${money(duplicate.checkAmount)}).` : `A Closing already exists for check #${duplicate.checkNumber}.`}</p>
          )}

          <div className="flow-block-label" style={{ marginTop: '.5rem' }}>{es ? 'Encabezado (datos del reclamo)' : 'Header (claim data)'}</div>
          {row('Insured(s):', <span>{insuredNames(claimRecord) || <em className="flow-muted">{es ? 'Falta el asegurado (editar reclamo)' : 'Missing insured (edit claim)'}</em>}</span>)}
          {CLAIM_KEYS.map(key => (
            <React.Fragment key={key}>{row(CLAIM_LABELS[key], (
              <input type="text" className="cc-input" style={{ width: 240, textAlign: 'left', borderColor: claimFields[key] ? undefined : 'var(--ui-warning)' }}
                value={claimFields[key]} placeholder={es ? 'Falta — escríbelo aquí' : 'Missing — type it here'}
                onChange={e => setClaimFields(prev => ({ ...prev, [key]: e.target.value }))} />
            ))}</React.Fragment>
          ))}
          <p className="flow-note">{es ? 'Se toman del reclamo; si los cambias aquí, se guardan en el reclamo.' : 'These come from the claim; changes here are saved on the claim.'}</p>

          <div className="flow-block-label">{es ? 'Breakdown' : 'Breakdown'}</div>
          {row('Total Check Amount:', amountInput('checkAmount'))}
          {row('Public Adjuster Fee(If Applicable):', <>
            <input type="number" step="0.5" min="0" max="100" className="cc-input closing-pct" value={form.feePercent} placeholder="%" onChange={e => setField('feePercent', e.target.value)} />
            <span className="flow-muted">%</span>
            {amountInput('paFee', money(suggestPaFee(form.checkAmount, form.feePercent)) || '$')}
          </>)}
          {CLOSING_FEES.map(fee => (
            <React.Fragment key={fee.key}>{row(fee.label, amountInput(fee.key))}</React.Fragment>
          ))}
          {row('Net to Client:', <strong>{money(result.breakdown.netToClient) || '—'}</strong>, 'is-total')}

          <div className="cc-grid" style={{ marginTop: '.75rem' }}>
            <div className="cc-input-group">
              <label className="cc-label">{es ? 'Tipo de pago ("as a Coverage ___")' : 'Payment type ("as a Coverage ___")'}</label>
              <select className="cc-select" value={form.paymentType} onChange={e => setField('paymentType', e.target.value)}>
                <option value="">—</option>
                {PAYMENT_TYPES.map(p => <option key={p.value} value={p.value}>{es ? `${p.es} (${p.value})` : p.value}</option>)}
              </select>
            </div>
            <div className="cc-input-group">
              <label className="cc-label">{es ? 'Fecha del documento' : 'Document date'}</label>
              <input type="date" className="cc-input" value={form.date} onChange={e => setField('date', e.target.value)} />
            </div>
          </div>

          <p className="flow-note">
            {es ? 'Los renglones vacíos salen en blanco en el documento. Si no escribes el honorario del PA, se usa el % sugerido (cortado al centavo).' : 'Empty lines are left blank on the document. If the PA fee is empty, the suggested % is used (truncated to the cent).'}
          </p>
          {result.errors.map(e => <p key={e} className="closing-error">{e}</p>)}
          {result.missing.length > 0 && <p className="closing-warning">{es ? 'Saldrán en blanco: ' : 'Will be left blank: '}{result.missing.join(', ')}</p>}

          <div className="flow-actions">
            <button type="button" className="cd-btn cd-btn-check" disabled={busy} onClick={() => save()}><i className="fa-solid fa-floppy-disk"></i> {es ? 'Guardar' : 'Save'}</button>
            <button type="button" className="cd-btn cd-btn-invoice" disabled={busy || result.errors.length > 0} onClick={async () => {
              // Estado previo (si ya estaba enviado a firmar, generar el PDF no lo retrocede).
              const previousStatus = closings.find(c => c.id === editing.id)?.status || 'draft';
              const saved = await save({ keepOpen: true });
              if (saved) download(form, { ...saved, status: previousStatus });
            }}>
              <i className="fa-solid fa-file-pdf"></i> {es ? 'Guardar y generar PDF' : 'Save & generate PDF'}
            </button>
            <button type="button" className="cd-btn" onClick={() => setEditing(null)}>{es ? 'Cerrar' : 'Close'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClosingBuilder;
